/** Shared weekly lesson selection — keeps same weekday/time when available, ~7-day spacing as fallback. */

import { DateTime } from 'luxon';

export const MS_PER_DAY = 86400000;

/** Stable id for (day-of-week, clock time). Uses JS getDay(): 0=Sun … 6=Sat. */
export function slotPatternKey(s) {
  if (!s) return '';
  const m = s.minutes ?? 0;
  return `${s.dayOfWeek}-${s.hours24}-${m}`;
}

export function typicalTimeFromSlot(s) {
  if (!s) return null;
  return {
    dayOfWeek: s.dayOfWeek,
    hours24: s.hours24,
    minutes: s.minutes ?? 0,
  };
}

/**
 * Pick one slot for this calendar week.
 * - Week 1: best by match priority.
 * - Later weeks: prefer same weekday+time as the first lesson (anchor); otherwise require ~7-day gap from previous.
 *
 * @param {object} [options] — When `forFirstLesson` is true, do not restrict to "exact" tier only. Otherwise the first
 *   week in a range that contains both an exact (e.g. Mon) and a non-exact (e.g. Wed) slot can wrongly pick Mon,
 *   while the coach card shows Wed as the first recommended time. Use `cardPatternKey` to prefer the same weekday+time
 *   as the first line on the card.
 */
export function pickSlotForWeek(alternatives, prevSelected, anchorKey, sortByPriority, options = {}) {
  if (!alternatives?.length) return null;
  const { forFirstLesson, cardPatternKey, firstLessonYmd, studentIanaZone } = options;
  // If this week has exact student-request overlaps, keep selection within that set — but not for lesson 1, where the
  // UI is anchored to the first slot shown on the coach card.
  const exactAlternatives = alternatives.filter((s) => s?.isExact);
  const useExactOnly = !forFirstLesson && exactAlternatives.length > 0;
  const pool = useExactOnly ? exactAlternatives : alternatives;

  if (!prevSelected) {
    if (forFirstLesson && cardPatternKey) {
      const byPattern = pool.filter((s) => slotPatternKey(s) === cardPatternKey);
      if (byPattern.length) return sortByPriority(byPattern)[0];
    }
    if (forFirstLesson && firstLessonYmd && studentIanaZone) {
      const byYmd = pool.filter((s) => {
        if (!s?.utc) return false;
        const dt = DateTime.fromISO(String(s.utc), { setZone: true }).setZone(studentIanaZone);
        return dt.isValid && dt.toFormat('yyyy-LL-dd') === firstLessonYmd;
      });
      if (byYmd.length) return sortByPriority(byYmd)[0];
    }
    return sortByPriority(pool)[0];
  }

  const byAnchor = pool.filter((s) => slotPatternKey(s) === anchorKey);
  if (byAnchor.length > 0) {
    return sortByPriority(byAnchor)[0];
  }

  // ~7 days; 1h slack for DST / rounding so Wed→next Wed still qualifies if times align
  const minGapMs = 7 * MS_PER_DAY - 60 * 60 * 1000;
  const withGap = pool.filter((s) => (s.date - prevSelected.date) >= minGapMs);
  const finalPool = withGap.length ? withGap : pool;
  return sortByPriority(finalPool)[0];
}
